/* ============================================================================
   BookingDetailPage (rd-bookingdetail) — CWR reskin.
   Scroll-over-hero scaffold (shipped SessionDetailPage mechanics) + the confirmed
   booking-detail mockup sheet (docs/mockups/client-dashboard-redesign/booking-detail.html).
   Colors are the INJECTED per-tenant theme tokens only — no :root hex, no hardcoded
   hex (destructive red = the minted --danger token).
   ============================================================================ */

.page {
  position: fixed;
  inset: 0;
  background: var(--bg-page);
  color: var(--text-primary);
  overflow: hidden;
}
.scroll {
  position: absolute;
  inset: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  z-index: 2;
  padding-bottom: calc(var(--tg-safe-area-inset-bottom, env(safe-area-inset-bottom, 0px)) + 40px);
  overscroll-behavior: none;
}
.scroll.hasFoot {
  padding-bottom: calc(var(--tg-safe-area-inset-bottom, env(safe-area-inset-bottom, 0px)) + 110px);
}
.scroll::-webkit-scrollbar {
  display: none;
}

/* ── Status pill (top-right, mirrors the back-button band) ── */
.statusPill {
  position: fixed;
  top: calc(
    var(--tg-safe-area-inset-top, env(safe-area-inset-top, 0px)) +
      var(--tg-content-safe-area-inset-top, 0px) + 12px
  );
  right: 18px;
  z-index: 82;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 30px;
  padding: 0 12px;
  border-radius: 9999px;
  background: var(--glass-bg-strong);
  backdrop-filter: var(--glass-blur);
  border: 1px solid rgba(var(--accent-rgb), 0.35);
  font-size: 11.5px;
  font-weight: 600;
  color: var(--accent-hot);
}
.statusPill .dot {
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: var(--accent);
  box-shadow: 0 0 8px rgba(var(--accent-rgb), 0.8);
}
.statusPill.muted {
  border-color: var(--border-soft);
  color: var(--text-tertiary);
}
.statusPill.muted .dot {
  background: var(--text-muted);
  box-shadow: none;
}

/* ── Rising sheet ── */
.heroSpacer {
  height: 330px;
  flex-shrink: 0;
  pointer-events: none;
}
.sheet {
  position: relative;
  z-index: 1;
  border-radius: 48px 48px 0 0;
  background: var(--bg-page);
  /* Upward only: the negative spread keeps the blur from spilling past the flat
     bottom edge, which drew a dark band under a short sheet on non-black looks. */
  box-shadow: 0 -10px 16px -6px rgba(0, 0, 0, 0.24);
  min-height: 480px;
}
.grab {
  width: 38px;
  height: 5px;
  border-radius: 3px;
  background: rgba(var(--fg-page-rgb), 0.22);
  margin: -4px auto 0;
}
.body {
  padding: 16px 20px 0;
  display: flex;
  flex-direction: column;
  gap: 18px;
}

/* ── 2.7 detail rows ── */
.det {
  display: flex;
  flex-direction: column;
  margin-top: -4px;
}
.det .row {
  display: grid;
  grid-template-columns: 20px 1fr auto;
  gap: 14px;
  align-items: center;
  padding: 14px 0;
}
.det .row + .row {
  border-top: 1px solid var(--border-subtle);
}
.det .row > svg {
  width: 18px;
  height: 18px;
  color: var(--accent);
  stroke-width: 1.85;
}
.lbl {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--text-tertiary);
}
.val {
  font-size: 14.5px;
  color: var(--text-primary);
  font-weight: 500;
  margin-top: 2px;
}
.val.dim {
  color: var(--text-secondary);
}
.sub {
  font-size: 12px;
  color: var(--text-secondary);
  margin-top: 2px;
}
.amt {
  font-size: 15px;
  font-weight: 700;
  color: var(--accent);
}
.amt.dim {
  color: var(--text-secondary);
}

/* client-booking-refund-details (mockup booking-detail-refunds.html, layout A):
   refund lines inside the Payment row, then the net paid. */
.det .row.top {
  align-items: start;
}
.det .row.top > svg {
  margin-top: 2px;
}
.minw {
  min-width: 0;
}
.chip {
  display: inline-block;
  font-size: 10.5px;
  font-weight: 600;
  line-height: 1;
  padding: 4px 8px;
  border-radius: 999px;
  vertical-align: 1px;
  background: rgba(var(--accent-rgb), 0.12);
  color: var(--accent-hot);
  border: 1px solid rgba(var(--accent-rgb), 0.28);
}
.chip.chipDone {
  background: rgba(var(--fg-page-rgb), 0.07);
  color: var(--text-secondary);
  border-color: var(--border-soft);
}
.ledger {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px dashed var(--border-soft);
}
.lline {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 10px;
  align-items: baseline;
  font-size: 13px;
  color: var(--text-secondary);
}
.lline .what {
  min-width: 0;
}
.lline .when {
  color: var(--text-tertiary);
  font-size: 12px;
}
.lline .money {
  font-variant-numeric: tabular-nums;
  font-weight: 600;
  color: var(--text-secondary);
}
.lline.net {
  color: var(--text-primary);
  font-weight: 600;
  padding-top: 8px;
  border-top: 1px solid var(--border-subtle);
}
.lline.net .money {
  color: var(--accent);
  font-weight: 700;
  font-size: 15px;
}
.chev {
  width: 16px;
  height: 16px;
  color: var(--text-tertiary);
  stroke-width: 2.2;
}
.rowBtn {
  appearance: none;
  -webkit-appearance: none;
  width: 34px;
  height: 34px;
  padding: 0;
  border-radius: 50%;
  border: 1px solid rgba(var(--accent-rgb), 0.5);
  background: transparent;
  box-shadow: none;
  color: var(--accent);
  display: grid;
  place-items: center;
  flex-shrink: 0;
  cursor: pointer;
}
.rowBtn svg {
  width: 16px;
  height: 16px;
  stroke-width: 2;
}
.whereMain {
  grid-column: 2 / 4;
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  text-align: left;
  background: none;
  border: none;
  color: inherit;
  font: inherit;
  padding: 0;
  min-width: 0;
}
.whereVirtualMain {
  grid-column: 2 / 3;
  min-width: 0;
}
/* location-notes-to-client: the business's notes under the Where line, below the map tap */
.whereNote {
  grid-column: 2 / 4;
  margin-top: -6px;
  font-size: 13px;
  line-height: 1.45;
  color: var(--text-primary);
  white-space: pre-line;
  overflow-wrap: anywhere;
}
.whereVirtualMainFull {
  grid-column: 2 / 4;
}
.whereText {
  appearance: none;
  -webkit-appearance: none;
  width: 100%;
  padding: 0;
  border: 0;
  background: transparent;
  color: inherit;
  font: inherit;
  text-align: left;
  cursor: pointer;
}
.copyChip {
  appearance: none;
  -webkit-appearance: none;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  margin-top: 6px;
  padding: 3px 9px;
  border-radius: 8px;
  background: rgba(var(--accent-rgb), 0.1);
  border: 1px solid rgba(var(--accent-rgb), 0.22);
  color: var(--accent-hot);
  font-size: 11.5px;
  font-weight: 600;
  cursor: pointer;
}
.copyChip svg {
  width: 12px;
  height: 12px;
  stroke-width: 2.2;
}
.copyMono {
  font-family: ui-monospace, Menlo, monospace;
  letter-spacing: 0.04em;
}

/* ── Section headings + prep/about ── */
.secH {
  font-family: var(--client-font-heading, 'EB Garamond', Georgia, serif);
  font-weight: 600;
  font-size: 19px;
  letter-spacing: -0.005em;
  color: var(--text-primary);
  margin-bottom: 8px;
}
.prepCard {
  position: relative;
  background: var(--glass-bg-strong);
  backdrop-filter: var(--glass-blur);
  border-radius: 18px;
  box-shadow: var(--glass-shadow);
  padding: 15px 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.prepCard::before {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: inherit;
  padding: 1px;
  background: linear-gradient(135deg, rgba(var(--accent-light-rgb), 0.18) 0%, rgba(var(--accent-light-rgb), 0.03) 100%);
  -webkit-mask: linear-gradient(black 0 0) content-box, linear-gradient(black 0 0);
  -webkit-mask-composite: xor;
  mask-composite: exclude;
  pointer-events: none;
}
.pcKicker {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: var(--accent);
}

/* ── Notice card ── */
.notice {
  display: flex;
  gap: 11px;
  align-items: flex-start;
  padding: 12px 13px;
  border-radius: 14px;
  background: rgba(var(--accent-rgb), 0.08);
  border: 1px solid rgba(var(--accent-rgb), 0.22);
}
.notice svg {
  width: 17px;
  height: 17px;
  color: var(--accent-hot);
  flex-shrink: 0;
  margin-top: 1px;
  stroke-width: 2;
}
.noticeText {
  font-size: 12.5px;
  color: var(--text-secondary);
  line-height: 1.45;
}
.noticeText b {
  color: var(--text-primary);
}

/* ── Cancel link (danger) ── */
.cancelRow {
  text-align: center;
  padding: 6px 0 12px;
}
.cancelLink {
  appearance: none;
  -webkit-appearance: none;
  padding: 0;
  border: 0;
  border-radius: 0;
  background: transparent;
  box-shadow: none;
  font-family: inherit;
  font-size: 13px;
  font-weight: 500;
  color: var(--danger);
  text-decoration: underline;
  text-underline-offset: 3px;
  text-decoration-color: rgba(var(--danger-rgb), 0.35);
  cursor: pointer;
}

/* booking-truth-batch: the closed-window line in the cancel link slot. */
.cancelClosed {
  margin: 0;
  font-size: 13px;
  line-height: 1.4;
  color: var(--text-secondary);
}

.refundPolicy {
  margin-top: 12px;
  padding: 11px 13px;
  border-radius: var(--r-md, 14px);
  background: var(--bg-inset);
  border: 1px solid var(--border-subtle);
  font-size: 12.5px;
  color: var(--text-secondary);
  line-height: 1.5;
  display: flex;
  gap: 9px;
  align-items: flex-start;
}
.refundPolicy svg {
  width: 15px;
  height: 15px;
  color: var(--accent);
  flex-shrink: 0;
  margin-top: 1px;
  stroke-width: 2;
}

/* ── Floating errand bar ── */
.footBar {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 70;
  padding: 20px 16px calc(var(--tg-safe-area-inset-bottom, env(safe-area-inset-bottom, 0px)) + 26px);
  background: linear-gradient(180deg, rgba(var(--bg-page-rgb), 0) 0%, rgba(var(--bg-page-rgb), 0.88) 32%, var(--bg-page) 100%);
}
.btnPrimary {
  appearance: none;
  -webkit-appearance: none;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 14px;
  border-radius: 14px;
  border: none;
  background: var(--accent-orb);
  color: var(--text-on-accent);
  font-size: 15.5px;
  font-weight: 700;
  letter-spacing: -0.01em;
  box-shadow: var(--glow-amber);
}
.btnPrimary svg {
  width: 17px;
  height: 17px;
  stroke-width: 2.6;
}

/* ── Cancellation progress + completion ── */
.cancelFeedbackBackdrop {
  position: fixed;
  inset: 0;
  z-index: 1200;
  display: grid;
  place-items: center;
  padding: 28px;
  background: rgba(var(--bg-page-rgb), 0.72);
  backdrop-filter: blur(8px) saturate(115%);
  animation: cancelBackdropIn 0.18s ease-out both;
}
.cancelProgress,
.cancelSuccessCard {
  background: var(--glass-bg-strong);
  backdrop-filter: var(--glass-blur);
  box-shadow: var(--glass-shadow);
  color: var(--text-primary);
}
.cancelProgress {
  min-width: 142px;
  padding: 20px 24px;
  border-radius: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 11px;
  font-size: 14px;
  font-weight: 650;
}
.cancelSpinner {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 3px solid var(--border-subtle);
  border-top-color: var(--accent);
  animation: cancelSpin 0.72s linear infinite;
}
.cancelSuccessCard {
  width: min(100%, 310px);
  border-radius: 24px;
  padding: 26px 22px 22px;
  text-align: center;
  animation: cancelCardIn 0.24s cubic-bezier(0.16, 1, 0.3, 1) both;
}
.cancelSuccessIcon {
  width: 48px;
  height: 48px;
  margin: 0 auto 14px;
  border-radius: 50%;
  display: grid;
  place-items: center;
  background: var(--status-pill-bg);
  color: var(--status-pill-text);
  font-size: 24px;
  font-weight: 700;
}
.cancelSuccessCard h2 {
  margin: 0;
  font-family: var(--font-display);
  font-size: 24px;
  line-height: 1.1;
  color: var(--text-primary);
}
.cancelSuccessCard p {
  margin: 9px 0 20px;
  font-size: 13.5px;
  line-height: 1.5;
  color: var(--text-secondary);
}
.cancelSuccessButton {
  appearance: none;
  width: 100%;
  border: 0;
  border-radius: 14px;
  padding: 13px 16px;
  background: var(--accent-orb);
  color: var(--text-on-accent);
  font: inherit;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
}
@keyframes cancelSpin {
  to { transform: rotate(360deg); }
}
@keyframes cancelBackdropIn {
  from { opacity: 0; }
}
@keyframes cancelCardIn {
  from { opacity: 0; transform: scale(0.92) translateY(8px); }
}
@media (prefers-reduced-motion: reduce) {
  .cancelFeedbackBackdrop,
  .cancelSuccessCard,
  .cancelSpinner {
    animation-duration: 0.01ms;
  }
}
